import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { formatCurrency, formatDate, getStatusLabel, formatMonth, getPaymentTypeLabel } from '../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  User, 
  Phone, 
  Mail, 
  CreditCard, 
  Home,
  Calendar,
  Wallet,
  Image,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DetailPenghuni = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [payments, setPayments] = useState([]);
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTenantData = async () => {
    try {
      // Fetch tenant details
      const tenantRes = await axios.get(`${API_URL}/api/tenants/${id}`);
      setTenant(tenantRes.data);

      // Fetch room details
      if (tenantRes.data.room_id) {
        try {
          const roomRes = await axios.get(`${API_URL}/api/rooms/${tenantRes.data.room_id}`);
          setRoom(roomRes.data);
        } catch (roomError) {
          console.error('Error fetching room:', roomError);
        }
      }

      // Fetch tenant payments
      try {
        const paymentsRes = await axios.get(`${API_URL}/api/payments`);
        const allPayments = paymentsRes.data.data || paymentsRes.data || [];
        const tenantPayments = allPayments.filter(p => p.tenant_id === id);
        setPayments(tenantPayments);
      } catch (paymentError) {
        console.error('Error fetching payments:', paymentError);
        setPayments([]);
      }

    } catch (error) {
      console.error('Error fetching tenant data:', error);
      toast.error('Gagal memuat data penghuni');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenantData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 spinner"></div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Penghuni tidak ditemukan</p>
        <Button variant="outline" onClick={() => navigate('/penghuni')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Kembali
        </Button>
      </div>
    );
  }

  const totalPaid = payments.filter(p => p.status === 'lunas').reduce((sum, p) => sum + p.amount, 0);
  const totalUnpaid = payments.filter(p => p.status === 'belum_bayar').reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="space-y-6" data-testid="tenant-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate('/penghuni')}
          className="rounded-full"
          data-testid="back-to-penghuni"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-heading font-bold">Detail Penghuni</h1>
          <p className="text-muted-foreground">Informasi lengkap penghuni kost</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Personal Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <User className="w-5 h-5" />
                Informasi Pribadi
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-8 h-8 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold">{tenant.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={`status-${tenant.status}`}>
                      {getStatusLabel(tenant.status)}
                    </Badge>
                    {tenant.user_id && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        Linked Account
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <Mail className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{tenant.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <Phone className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">No. Telepon</p>
                    <p className="font-medium">{tenant.phone}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">No. KTP</p>
                    <p className="font-medium">{tenant.id_number}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <Home className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Kamar</p>
                    <p className="font-medium">{tenant.room_name}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* KTP Photo Card */}
          {tenant.ktp_photo && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Foto KTP
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-xl overflow-hidden border bg-muted">
                  <img 
                    src={tenant.ktp_photo.startsWith('http') ? tenant.ktp_photo : `${API_URL}${tenant.ktp_photo}`} 
                    alt="Foto KTP" 
                    className="w-full max-h-[300px] object-contain"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment History Card */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Riwayat Pembayaran
              </CardTitle>
              <CardDescription>
                Daftar semua transaksi pembayaran penghuni
              </CardDescription>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Belum ada riwayat pembayaran</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bulan</TableHead>
                      <TableHead>Jenis</TableHead>
                      <TableHead>Jumlah</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell className="font-medium">
                          {formatMonth(payment.month)}
                        </TableCell>
                        <TableCell>{getPaymentTypeLabel(payment.payment_type)}</TableCell>
                        <TableCell>{formatCurrency(payment.amount)}</TableCell>
                        <TableCell>
                          <Badge className={`status-${payment.status === 'lunas' ? 'aktif' : 'nonaktif'}`}>
                            {payment.status === 'lunas' ? (
                              <><CheckCircle className="w-3 h-3 mr-1" /> Lunas</>
                            ) : (
                              <><Clock className="w-3 h-3 mr-1" /> Belum Bayar</>
                            )}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Summary Cards */}
        <div className="space-y-6">
          {/* Deposit Card */}
          <Card className={tenant.deposit > 0 ? "border-green-200 bg-green-50/50" : "border-orange-200 bg-orange-50/50"}>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                Deposit / Uang Jaminan
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-3xl font-bold ${tenant.deposit > 0 ? 'text-green-600' : 'text-orange-600'}`}>
                {formatCurrency(tenant.deposit)}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {tenant.deposit > 0 
                  ? "Deposit sudah dibayar" 
                  : "Belum ada deposit"}
              </p>
            </CardContent>
          </Card>

          {/* Room Info Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Home className="w-5 h-5" />
                Info Kamar
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Nama Kamar</p>
                <p className="font-semibold text-lg">{tenant.room_name}</p>
              </div>
              {room && (
                <div>
                  <p className="text-sm text-muted-foreground">Harga Sewa</p>
                  <p className="font-semibold text-lg text-primary">{formatCurrency(room.price)}/bulan</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Rental Period Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Periode Sewa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Mulai Sewa</p>
                <p className="font-semibold">{formatDate(tenant.start_date)}</p>
              </div>
              {tenant.end_date && (
                <div>
                  <p className="text-sm text-muted-foreground">Berakhir</p>
                  <p className="font-semibold">{formatDate(tenant.end_date)}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Summary Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Ringkasan Pembayaran
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Total Dibayar</span>
                <span className="font-semibold text-green-600">{formatCurrency(totalPaid)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Belum Dibayar</span>
                <span className={`font-semibold ${totalUnpaid > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                  {formatCurrency(totalUnpaid)}
                </span>
              </div>
              {totalUnpaid > 0 && (
                <div className="pt-2 border-t">
                  <div className="flex items-center gap-2 text-sm text-orange-600">
                    <AlertCircle className="w-4 h-4" />
                    Ada tagihan belum dibayar
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DetailPenghuni;
